import {jsPDF} from 'jspdf'
import licitoLogoUrl from '../assets/licito-logo-branca.png'
import {AnalyzedItem, AnalyzedItemResponse} from 'types/analyzedItens'

type RGB = [number, number, number]

type PdfTheme = {
    primary: RGB
    accent: RGB
    bodyText: RGB
    contentText: RGB
    mutedText: RGB
    subtleText: RGB
    border: RGB
    softBorder: RGB
    cardSurface: RGB
    onDark: RGB
    onDarkMuted: RGB
    iconOnColor: RGB
}

type StatusStyle = {
    label: string
    plural: string
    color: RGB
    soft: RGB
}

const THEME: PdfTheme = {
    primary: [58, 205, 180],
    accent: [255, 255, 255],
    bodyText: [27, 36, 48],
    contentText: [70, 80, 92],
    mutedText: [110, 120, 132],
    subtleText: [150, 160, 172],
    border: [222, 228, 235],
    softBorder: [236, 240, 245],
    cardSurface: [255, 255, 255],
    onDark: [255, 255, 255],
    onDarkMuted: [200, 212, 226],
    iconOnColor: [255, 255, 255],
}

const STATUS_STYLES: Record<number, StatusStyle> = {
    0: {
        label: 'Positivo',
        plural: 'Positivos',
        color: [40, 150, 95],
        soft: [233, 246, 238],
    },
    1: {
        label: 'Alerta',
        plural: 'Alertas',
        color: [208, 150, 28],
        soft: [253, 244, 220],
    },
    2: {
        label: 'Crítico',
        plural: 'Críticos',
        color: [200, 55, 70],
        soft: [253, 232, 232],
    },
}

const FALLBACK_STATUS: StatusStyle = {
    label: 'Info',
    plural: 'Informativos',
    color: [56, 110, 196],
    soft: [229, 238, 252],
}

const LAYOUT = {
    margin: 16,
    pageWidth: 210,
    pageHeight: 297,
    heroHeaderHeight: 36,
    compactHeaderHeight: 18,
    footerY: 286,
    contentBottom: 278,
}

const getStatusStyle = (status: number): StatusStyle =>
    STATUS_STYLES[status] || FALLBACK_STATUS

const formatDocumentName = (value?: string): string =>
    value && value.trim() ? value.trim() : 'Análise de documento'

const setFill = (doc: jsPDF, rgb: RGB) => doc.setFillColor(...rgb)
const setText = (doc: jsPDF, rgb: RGB) => doc.setTextColor(...rgb)
const setDraw = (doc: jsPDF, rgb: RGB) => doc.setDrawColor(...rgb)

type LogoData = {url: string; aspectRatio: number}

const LOGO_ASPECT_RATIO = 1828 / 468

let cachedLogo: Promise<LogoData | null> | null = null

const getLogoDataUrl = async (): Promise<LogoData | null> => {
    if (!cachedLogo) {
        cachedLogo = fetch(licitoLogoUrl)
            .then((response) => response.blob())
            .then(
                (blob) =>
                    new Promise<string>((resolve, reject) => {
                        const reader = new FileReader()
                        reader.onload = () => resolve(reader.result as string)
                        reader.onerror = reject
                        reader.readAsDataURL(blob)
                    })
            )
            .then((url) => ({url, aspectRatio: LOGO_ASPECT_RATIO}))
            .catch(() => null)
    }
    return cachedLogo
}

const drawStatusIcon = (
    doc: jsPDF,
    centerX: number,
    centerY: number,
    radius: number,
    status: number
) => {
    const style = getStatusStyle(status)

    setFill(doc, style.color)
    doc.circle(centerX, centerY, radius, 'F')

    doc.setLineCap('round')
    doc.setLineJoin('round')

    if (status === 2) {
        // X
        setDraw(doc, THEME.iconOnColor)
        doc.setLineWidth(0.7)
        const offset = radius * 0.45
        doc.line(
            centerX - offset,
            centerY - offset,
            centerX + offset,
            centerY + offset
        )
        doc.line(
            centerX - offset,
            centerY + offset,
            centerX + offset,
            centerY - offset
        )
    } else if (status === 1) {
        // !
        setFill(doc, THEME.iconOnColor)
        const barHalfWidth = radius * 0.13
        const barTopY = centerY - radius * 0.5
        const barHeight = radius * 0.75
        doc.rect(
            centerX - barHalfWidth,
            barTopY,
            barHalfWidth * 2,
            barHeight,
            'F'
        )
        doc.circle(centerX, centerY + radius * 0.55, radius * 0.16, 'F')
    } else if (status === 0) {
        // checkmark
        setDraw(doc, THEME.iconOnColor)
        doc.setLineWidth(0.75)
        const ax = centerX - radius * 0.5
        const ay = centerY + radius * 0.05
        const bx = centerX - radius * 0.1
        const by = centerY + radius * 0.45
        const cx = centerX + radius * 0.55
        const cy = centerY - radius * 0.35
        doc.line(ax, ay, bx, by)
        doc.line(bx, by, cx, cy)
    } else {
        // i
        setFill(doc, THEME.iconOnColor)
        doc.circle(centerX, centerY - radius * 0.45, radius * 0.16, 'F')
        const barHalfWidth = radius * 0.13
        doc.rect(
            centerX - barHalfWidth,
            centerY - radius * 0.15,
            barHalfWidth * 2,
            radius * 0.7,
            'F'
        )
    }

    doc.setLineCap('butt')
    doc.setLineJoin('miter')
}

const drawHeroHeader = (
    doc: jsPDF,
    documentName: string,
    generatedAt: string,
    logo: LogoData | null,
    docTypeLabel?: string
): number => {
    const pageWidth = LAYOUT.pageWidth
    const headerHeight = LAYOUT.heroHeaderHeight
    const margin = LAYOUT.margin

    setFill(doc, THEME.primary)
    doc.rect(0, 0, pageWidth, headerHeight, 'F')

    setFill(doc, THEME.accent)
    doc.rect(0, headerHeight, pageWidth, 1.2, 'F')

    setText(doc, THEME.accent)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(8)
    doc.text('RELATÓRIO DE CONSULTA', margin, 13)

    setText(doc, THEME.onDark)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(15)
    const titleMaxWidth = pageWidth - margin * 2 - 40
    const titleLines = doc.splitTextToSize(documentName, titleMaxWidth)
    doc.text(titleLines[0], margin, 22)

    if (docTypeLabel) {
        setText(doc, THEME.onDarkMuted)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(7.5)
        doc.text(docTypeLabel, margin, 27)
    }

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    setText(doc, THEME.onDark)
    doc.text(`Gerado em ${generatedAt}`, margin, 32.5)

    if (logo) {
        const logoHeight = 10
        const logoWidth = logoHeight * logo.aspectRatio
        doc.addImage(
            logo.url,
            'PNG',
            pageWidth - margin - logoWidth,
            9,
            logoWidth,
            logoHeight
        )
    }

    setText(doc, THEME.bodyText)
    return headerHeight + 10
}

const drawCompactHeader = (
    doc: jsPDF,
    documentName: string,
    logo: LogoData | null,
    docTypeLabel?: string
): number => {
    const pageWidth = LAYOUT.pageWidth
    const headerHeight = LAYOUT.compactHeaderHeight
    const margin = LAYOUT.margin

    setFill(doc, THEME.primary)
    doc.rect(0, 0, pageWidth, headerHeight, 'F')

    setFill(doc, THEME.accent)
    doc.rect(0, headerHeight, pageWidth, 0.8, 'F')

    const titleMaxWidth = pageWidth - margin * 2 - 36
    const titleLines = doc.splitTextToSize(documentName, titleMaxWidth)

    if (docTypeLabel) {
        setText(doc, THEME.onDarkMuted)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(6)
        doc.text(docTypeLabel, margin, 6.5)

        setText(doc, THEME.onDark)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(9)
        doc.text(titleLines[0], margin, 14)
    } else {
        setText(doc, THEME.onDark)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(10)
        doc.text(titleLines[0], margin, 11.5)
    }

    if (logo) {
        const logoHeight = 7
        const logoWidth = logoHeight * logo.aspectRatio
        doc.addImage(
            logo.url,
            'PNG',
            pageWidth - margin - logoWidth,
            3.5,
            logoWidth,
            logoHeight
        )
    }

    setText(doc, THEME.bodyText)
    return headerHeight + 8
}

const drawSummary = (
    doc: jsPDF,
    items: AnalyzedItemResponse,
    startY: number
): number => {
    const margin = LAYOUT.margin
    const contentWidth = LAYOUT.pageWidth - margin * 2

    const counts = items.reduce(
        (acc, item) => {
            acc.total += 1
            if (item.status === 0) acc.positive += 1
            else if (item.status === 1) acc.alert += 1
            else if (item.status === 2) acc.critical += 1
            return acc
        },
        {total: 0, positive: 0, alert: 0, critical: 0}
    )

    setText(doc, THEME.subtleText)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(7.5)
    doc.text('SÍNTESE DA CONSULTA', margin, startY)

    setText(doc, THEME.mutedText)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    const totalLabel = `${counts.total} ${
        counts.total === 1 ? 'item analisado' : 'itens analisados'
    }`
    doc.text(totalLabel, LAYOUT.pageWidth - margin, startY, {align: 'right'})

    setDraw(doc, THEME.softBorder)
    doc.setLineWidth(0.2)
    doc.line(margin, startY + 2.5, LAYOUT.pageWidth - margin, startY + 2.5)

    const groups = [
        {
            label: 'Críticos',
            count: counts.critical,
            color: STATUS_STYLES[2].color,
        },
        {label: 'Alertas', count: counts.alert, color: STATUS_STYLES[1].color},
        {
            label: 'Positivos',
            count: counts.positive,
            color: STATUS_STYLES[0].color,
        },
    ]

    const circleRadius = 6.5
    const groupCenterY = startY + 16
    const groupSpan = contentWidth / groups.length

    groups.forEach((group, index) => {
        const centerX = margin + groupSpan * index + groupSpan / 2

        setFill(doc, group.color)
        doc.circle(centerX, groupCenterY, circleRadius, 'F')

        setText(doc, THEME.iconOnColor)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(14)
        doc.text(String(group.count), centerX, groupCenterY + 1.6, {
            align: 'center',
        })

        setText(doc, THEME.mutedText)
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
        doc.text(group.label, centerX, groupCenterY + circleRadius + 5, {
            align: 'center',
        })
    })

    return groupCenterY + circleRadius + 12
}

const drawSectionLabel = (doc: jsPDF, y: number): number => {
    const margin = LAYOUT.margin

    setText(doc, THEME.subtleText)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(7.5)
    doc.text('ITENS ANALISADOS', margin, y)

    setDraw(doc, THEME.softBorder)
    doc.setLineWidth(0.2)
    doc.line(margin, y + 2.2, LAYOUT.pageWidth - margin, y + 2.2)

    setText(doc, THEME.bodyText)
    return y + 7
}

type InlineSegment = {text: string; bold: boolean}
type ContentBlock = {segments: InlineSegment[]; bullet: boolean}
type ContentWord = {text: string; bold: boolean}
type WrappedLine = {words: ContentWord[]; indent: number}

const parseInlineSegments = (text: string): InlineSegment[] => {
    const segments: InlineSegment[] = []
    const regex = /\*\*(.+?)\*\*|__(.+?)__/g
    let lastIndex = 0
    let match: RegExpExecArray | null

    while ((match = regex.exec(text))) {
        if (match.index > lastIndex) {
            segments.push({text: text.slice(lastIndex, match.index), bold: false})
        }
        segments.push({text: match[1] ?? match[2] ?? '', bold: true})
        lastIndex = regex.lastIndex
    }
    if (lastIndex < text.length) {
        segments.push({text: text.slice(lastIndex), bold: false})
    }

    return segments.filter((segment) => segment.text.length > 0)
}

const parseMarkdownBlocks = (content: string): ContentBlock[] => {
    const lines = content.split(/\r?\n/)
    const blocks: ContentBlock[] = []
    let paragraphBuffer: string[] = []

    const flushParagraph = () => {
        if (paragraphBuffer.length === 0) return
        const text = paragraphBuffer.join(' ').trim()
        if (text) {
            blocks.push({segments: parseInlineSegments(text), bullet: false})
        }
        paragraphBuffer = []
    }

    lines.forEach((rawLine) => {
        const line = rawLine.trim()
        if (!line) {
            flushParagraph()
            return
        }

        const bulletMatch = line.match(/^[-*+]\s+(.*)$/)
        const orderedMatch = line.match(/^\d+\.\s+(.*)$/)
        const headingMatch = line.match(/^#{1,6}\s+(.*)$/)

        if (bulletMatch) {
            flushParagraph()
            blocks.push({segments: parseInlineSegments(bulletMatch[1]), bullet: true})
        } else if (orderedMatch) {
            flushParagraph()
            blocks.push({segments: parseInlineSegments(orderedMatch[1]), bullet: true})
        } else if (headingMatch) {
            flushParagraph()
            blocks.push({
                segments: parseInlineSegments(headingMatch[1]).map((segment) => ({
                    ...segment,
                    bold: true,
                })),
                bullet: false,
            })
        } else {
            paragraphBuffer.push(line)
        }
    })
    flushParagraph()

    return blocks.length > 0
        ? blocks
        : [{segments: parseInlineSegments(content), bullet: false}]
}

const wrapBlocksToLines = (
    doc: jsPDF,
    blocks: ContentBlock[],
    textWidth: number,
    fontSize: number
): WrappedLine[] => {
    const bulletIndent = 4
    const lines: WrappedLine[] = []

    const measure = (text: string, bold: boolean) => {
        doc.setFont('helvetica', bold ? 'bold' : 'normal')
        doc.setFontSize(fontSize)
        return doc.getTextWidth(text)
    }

    blocks.forEach((block) => {
        const indent = block.bullet ? bulletIndent : 0
        const availableWidth = textWidth - indent

        const words: ContentWord[] = []
        block.segments.forEach((segment) => {
            segment.text
                .split(/\s+/)
                .filter(Boolean)
                .forEach((word) => words.push({text: word, bold: segment.bold}))
        })
        if (words.length === 0) return

        if (block.bullet) {
            words[0] = {...words[0], text: `• ${words[0].text}`}
        }

        let currentWords: ContentWord[] = []
        let currentWidth = 0

        words.forEach((word) => {
            const wordWidth = measure(word.text, word.bold)
            const sepWidth = currentWords.length > 0 ? measure(' ', false) : 0

            if (
                currentWords.length > 0 &&
                currentWidth + sepWidth + wordWidth > availableWidth
            ) {
                lines.push({words: currentWords, indent})
                currentWords = [word]
                currentWidth = wordWidth
            } else {
                currentWords.push(word)
                currentWidth += sepWidth + wordWidth
            }
        })

        if (currentWords.length > 0) {
            lines.push({words: currentWords, indent})
        }
    })

    return lines
}

const drawWrappedLines = (
    doc: jsPDF,
    lines: WrappedLine[],
    x: number,
    firstBaselineY: number,
    lineHeight: number,
    fontSize: number
) => {
    lines.forEach((line, lineIndex) => {
        let cursorX = x + line.indent
        const baselineY = firstBaselineY + lineIndex * lineHeight

        line.words.forEach((word) => {
            doc.setFont('helvetica', word.bold ? 'bold' : 'normal')
            doc.setFontSize(fontSize)
            doc.text(word.text, cursorX, baselineY)
            cursorX += doc.getTextWidth(word.text) + doc.getTextWidth(' ')
        })
    })
}

type ItemLayout = {
    iconCenterX: number
    iconCenterY: number
    iconRadius: number
    textX: number
    textWidth: number
    titleLines: string[]
    titleLineHeight: number
    titleFirstBaselineY: number
    contentLines: WrappedLine[]
    contentLineHeight: number
    contentFontSize: number
    contentFirstBaselineY: number
    cardHeight: number
}

const computeItemLayout = (
    doc: jsPDF,
    item: AnalyzedItem,
    index: number,
    y: number
): ItemLayout => {
    const margin = LAYOUT.margin
    const contentWidth = LAYOUT.pageWidth - margin * 2
    const padX = 9
    const padTop = 8
    const padBottom = 9
    const iconRadius = 2.5
    const iconRightGap = 5.5
    const titleToContentGap = 6

    const iconCenterX = margin + padX + iconRadius
    const textX = iconCenterX + iconRadius + iconRightGap
    const textWidth = contentWidth - padX - (textX - margin)

    const title = item.analyzed_item_title || `Item ${index + 1}`
    const content = item.content || '-'
    const contentFontSize = 9.5

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10.5)
    const titleLines = doc.splitTextToSize(title, textWidth)
    const titleLineHeight = 5

    const contentBlocks = parseMarkdownBlocks(content)
    const contentLines = wrapBlocksToLines(
        doc,
        contentBlocks,
        textWidth,
        contentFontSize
    )
    const contentLineHeight = 4.7

    const titleFirstBaselineY = y + padTop + 3.2
    const titleLastBaselineY =
        titleFirstBaselineY + (titleLines.length - 1) * titleLineHeight
    const contentFirstBaselineY = titleLastBaselineY + titleToContentGap
    const contentLastBaselineY =
        contentFirstBaselineY + (contentLines.length - 1) * contentLineHeight

    const cardHeight = contentLastBaselineY - y + padBottom - 2

    return {
        iconCenterX,
        iconCenterY: titleFirstBaselineY - 1.5,
        iconRadius,
        textX,
        textWidth,
        titleLines,
        titleLineHeight,
        titleFirstBaselineY,
        contentLines,
        contentLineHeight,
        contentFontSize,
        contentFirstBaselineY,
        cardHeight,
    }
}

const drawItemCard = (
    doc: jsPDF,
    item: AnalyzedItem,
    index: number,
    y: number
): number => {
    const margin = LAYOUT.margin
    const contentWidth = LAYOUT.pageWidth - margin * 2
    const statusStyle = getStatusStyle(item.status)
    const layout = computeItemLayout(doc, item, index, y)

    setFill(doc, THEME.cardSurface)
    setDraw(doc, THEME.border)
    doc.setLineWidth(0.2)
    doc.roundedRect(margin, y, contentWidth, layout.cardHeight, 2.6, 2.6, 'FD')

    drawStatusIcon(
        doc,
        layout.iconCenterX,
        layout.iconCenterY,
        layout.iconRadius,
        item.status
    )

    setText(doc, statusStyle.color)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10.5)
    layout.titleLines.forEach((line, lineIndex) => {
        doc.text(
            line,
            layout.textX,
            layout.titleFirstBaselineY + lineIndex * layout.titleLineHeight
        )
    })

    setText(doc, THEME.contentText)
    drawWrappedLines(
        doc,
        layout.contentLines,
        layout.textX,
        layout.contentFirstBaselineY,
        layout.contentLineHeight,
        layout.contentFontSize
    )

    return layout.cardHeight + 5
}

const drawFooters = (doc: jsPDF) => {
    const totalPages = doc.getNumberOfPages()
    const margin = LAYOUT.margin
    const pageWidth = LAYOUT.pageWidth

    for (let page = 1; page <= totalPages; page += 1) {
        doc.setPage(page)

        setDraw(doc, THEME.softBorder)
        doc.setLineWidth(0.2)
        doc.line(
            margin,
            LAYOUT.footerY - 4,
            pageWidth - margin,
            LAYOUT.footerY - 4
        )

        setText(doc, THEME.subtleText)
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(8)
        doc.text(
            'licito.guru · Análise automatizada de documentos de licitação',
            margin,
            LAYOUT.footerY
        )
        doc.text(
            `Página ${page} de ${totalPages}`,
            pageWidth - margin,
            LAYOUT.footerY,
            {align: 'right'}
        )
    }
}

export const generateAnalysisPdf = async (
    items: AnalyzedItemResponse,
    documentName: string,
    docTypeLabel?: string
) => {
    const doc = new jsPDF({unit: 'mm', format: 'a4'})
    const margin = LAYOUT.margin
    const generatedAt = new Date().toLocaleString('pt-BR')
    const logoDataUrl = await getLogoDataUrl()
    const safeName = formatDocumentName(documentName)

    let yPosition = drawHeroHeader(
        doc,
        safeName,
        generatedAt,
        logoDataUrl,
        docTypeLabel
    )

    if (!items || items.length === 0) {
        setText(doc, THEME.mutedText)
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(11)
        doc.text('Nenhum item analisado disponível.', margin, yPosition + 6)
        drawFooters(doc)
        doc.save(`${safeName}.pdf`)
        return
    }

    yPosition = drawSummary(doc, items, yPosition)
    yPosition = drawSectionLabel(doc, yPosition)

    items.forEach((analysisItem, index) => {
        const projected = computeItemLayout(doc, analysisItem, index, yPosition)

        if (yPosition + projected.cardHeight > LAYOUT.contentBottom) {
            doc.addPage()
            yPosition = drawCompactHeader(
                doc,
                safeName,
                logoDataUrl,
                docTypeLabel
            )
            yPosition = drawSectionLabel(doc, yPosition)
        }

        const consumed = drawItemCard(doc, analysisItem, index, yPosition)
        yPosition += consumed
    })

    drawFooters(doc)
    doc.save(`${safeName}.pdf`)
}
